import { SESSION_TIMEOUT_MS } from './constants';
import type { IPersonalizationStorageAdapter } from './types';

/**
 * Generates a unique session ID.
 * Format: `{timestamp}.{random}` (e.g., "1707653241.a8b3c9")
 *
 * @returns {string} Unique session ID
 * @private
 */
function generateSessionId(): string {
  const timestamp = Math.floor(Date.now() / 1000);
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}.${random}`;
}

/**
 * Retrieves the current session ID or creates a new one if expired.
 * Sessions expire after 30 minutes of inactivity (SESSION_TIMEOUT_MS).
 *
 * Session lifecycle:
 * - If no existing session or session expired (>30min): creates new session ID
 * - If session exists and active (<30min): updates timestamp and returns existing ID
 *
 * Storage keys used:
 * - `ss_p_session_id`: Session identifier (sent to server)
 * - `ss_p_session_ts`: Last activity timestamp (client-side only)
 *
 * @param {IPersonalizationStorageAdapter} adapter - Storage adapter for session persistence
 * @returns {string} Current or newly created session ID
 *
 * @example
 * const sessionId = getOrCreateSessionId(storageAdapter);
 * // Returns: "1707653241.a8b3c9" (existing or new)
 *
 * // After 5 minutes of activity
 * const sameSessionId = getOrCreateSessionId(storageAdapter);
 * // Returns: "1707653241.a8b3c9" (same session, timestamp updated)
 *
 * // After 31 minutes of inactivity
 * const newSessionId = getOrCreateSessionId(storageAdapter);
 * // Returns: "1707656841.x9y2z1" (new session created)
 */
export function getOrCreateSessionId(adapter: IPersonalizationStorageAdapter): string {
  const existing = adapter.getSessionId();
  const ts = adapter.getSessionTimestamp();
  const now = Date.now();

  if (existing && ts && now - ts < SESSION_TIMEOUT_MS) {
    adapter.setSessionTimestamp(now);
    return existing;
  }

  const newId = generateSessionId();
  adapter.setSessionId(newId);
  adapter.setSessionTimestamp(now);
  return newId;
}
